import { z } from 'zod';
import { SEARCH_QUERY_MAX } from './catalog';

/** MangaDex ids are UUIDs. */
export const mangaIdSchema = z.uuid();
export const mangaParamsSchema = z.object({ mangaId: mangaIdSchema });

export const mangaSortSchema = z.enum(['relevance', 'newest', 'oldest', 'title']);
export type MangaSort = z.infer<typeof mangaSortSchema>;

/**
 * A manga series from MangaDex's catalogue that has an official English release. Libellus
 * shows the catalogue and sends readers to the official source; it never shows chapters.
 */
export const mangaSeriesSchema = z.object({
  id: mangaIdSchema,
  title: z.string(),
  /** The title in its own language, romanised, when it differs. */
  originalTitle: z.string().nullable(),
  authors: z.array(z.string()),
  year: z.number().int().nullable(),
  status: z.enum(['ongoing', 'completed', 'hiatus', 'cancelled']).nullable(),
  /** The cover through the Libellus API (MangaDex does not allow hotlinking), relative to it. */
  coverPath: z.string().nullable(),
  /** The official English edition: a publisher's reader or store. */
  official: z.object({ url: z.string(), name: z.string() }),
  mangadexUrl: z.string(),
});
export type MangaSeries = z.infer<typeof mangaSeriesSchema>;

export const mangaDetailSchema = mangaSeriesSchema.extend({
  description: z.string().nullable(),
  tags: z.array(z.string()),
});
export type MangaDetail = z.infer<typeof mangaDetailSchema>;

export const mangaSearchQuerySchema = z.object({
  q: z.string().trim().max(SEARCH_QUERY_MAX).default(''),
  sort: mangaSortSchema.default('relevance'),
  cursor: z.string().max(10).optional(),
});
export type MangaSearchQuery = z.infer<typeof mangaSearchQuerySchema>;

export const mangaSearchPageSchema = z.object({
  items: z.array(mangaSeriesSchema),
  nextCursor: z.string().nullable(),
});
export type MangaSearchPage = z.infer<typeof mangaSearchPageSchema>;

export const mangaCoverParamsSchema = z.object({
  mangaId: mangaIdSchema,
  file: z.string().regex(/^[0-9a-f-]{36}\.(jpg|jpeg|png|gif|webp)\.(256|512)\.jpg$/i),
});
